/**
 * RestroPulse POS · Sprint 2 Checkpoint B.2
 *
 * The bridge between the reducer's optimistic view and the backend
 * system of record. Every mutation flows through here so we can:
 *
 *   1. Apply the reducer's optimistic patch immediately (UI stays snappy).
 *   2. Persist to /api/pos/* with a STABLE event_id (contract §8):
 *      the same idempotency key is reused on timeout retries, so the
 *      server never applies the same intent twice.
 *   3. Merge the server's authoritative response back into the reducer
 *      (renaming any local temp IDs → server IDs).
 *   4. On rejection, roll the reducer back to the pre-optimistic
 *      snapshot AND surface the reason both as a toast (non-blocking,
 *      global) and as an inline error stamp on the affected order
 *      (visible on the line panel until dismissed by the next action).
 *
 * Invoices, payments and settlement stay explicitly LOCAL (Sprint 3
 * scope). Anything routed through `withLive()` here is a real API
 * call; everything else drops straight through to the reducer.
 */
import { toast } from "sonner";

import { ACT, findStaffById } from "./posReducer";
import { newEventId } from "./terminalBinding";
import {
  openOrder as apiOpenOrder,
  addLine as apiAddLine,
  fireTicket as apiFireTicket,
  holdOrder as apiHoldOrder,
  resumeOrder as apiResumeOrder,
  openBusinessDay as apiOpenBusinessDay,
  closeBusinessDay as apiCloseBusinessDay,
  openShift as apiOpenShift,
  closeShift as apiCloseShift,
  listOpenOrders as apiListOpenOrders,
  getSessionStatus as apiGetSessionStatus,
  printBill as apiPrintBill,
  reprintBill as apiReprintBill,
  addTender as apiAddTender,
  reverseTender as apiReverseTender,
  settleOrder as apiSettleOrder,
  voidInvoice as apiVoidInvoice,
  transferInvoiceToDues as apiTransferInvoiceToDues,
  splitOrder as apiSplitOrder,
  releaseDraftOrder as apiReleaseDraftOrder,
  voidOrder as apiVoidOrder,
  fetchUnresolvedOrders as apiFetchUnresolvedOrders,
} from "./posLive";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// A ULID/UUID minted once per retry-group. Callers hold on to this so
// subsequent retries reuse the same key and the server dedups.
function mintEventId() { return newEventId(); }

/** Retry a POST/PATCH/DELETE that timed out, using the SAME event_id.
 *  Any non-network error (400/401/403/404/409/…) surfaces immediately —
 *  those are decisions, not "message may not have landed" ambiguities.
 */
async function retryOnTimeout(fn, { attempts = 3, backoffMs = 400 } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const isTimeout =
        e?.code === "ECONNABORTED" ||
        e?.message?.toLowerCase?.().includes("timeout") ||
        e?.message?.toLowerCase?.().includes("network");
      if (!isTimeout) throw e;    // real server rejection — stop retrying
      if (i === attempts - 1) throw e;
      await new Promise((r) => setTimeout(r, backoffMs * (i + 1)));
    }
  }
  throw lastErr;
}

function extractReason(err) {
  // Financial Ground Truth (Feb 2026) · error normalisation.
  // FastAPI serialises `HTTPException(detail=...)` verbatim, and
  // `RequestValidationError` produces `{"detail": [ {loc, msg, type,
  // input}, ... ]}` — the array form is what caused the live
  // "Could not close shift: [object Object]" bug.
  //
  // Supported shapes:
  //   • string detail                     → return as-is
  //   • object detail (FastAPI custom)    → prefer .message / .msg /
  //                                          .reason / JSON.stringify
  //   • array of validation errors        → join each entry's .msg
  //                                          (loc + msg for context)
  //   • network / axios error             → err.message
  //   • no response body                  → generic fallback
  const stringify = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (Array.isArray(v)) {
      return v.map((entry) => {
        if (typeof entry === "string") return entry;
        if (entry && typeof entry === "object") {
          const loc = Array.isArray(entry.loc) ? entry.loc.join(".") : entry.loc;
          const msg = entry.msg || entry.message || entry.detail || "";
          return loc ? `${loc}: ${msg}` : msg;
        }
        return String(entry);
      }).filter(Boolean).join(" · ");
    }
    if (typeof v === "object") {
      // Prefer common human-readable keys before falling back to JSON.
      const candidate = v.message || v.msg || v.reason || v.detail;
      if (typeof candidate === "string") return candidate;
      if (candidate) return stringify(candidate);
      try { return JSON.stringify(v); } catch { return String(v); }
    }
    return String(v);
  };

  // 1. Axios: try response.data.detail first (FastAPI convention),
  //    then top-level data (some routes return { error, ... }).
  const data = err?.response?.data;
  if (data != null) {
    if (data.detail !== undefined) {
      const s = stringify(data.detail);
      if (s) return s;
    }
    const alt = data.error || data.message;
    if (alt) {
      const s = stringify(alt);
      if (s) return s;
    }
    // Last resort — serialise the whole body.
    const s = stringify(data);
    if (s && s !== "{}" && s !== "[]") return s;
  }
  // 2. Network / axios request never reached the server.
  if (err?.request && !err?.response) {
    return "Network error — the server did not respond. Try again.";
  }
  // 3. Anything else (SyntaxError, TypeError from the client itself)
  if (err?.message) return err.message;
  return "Server rejected the change.";
}

function surfaceError(order_id, reason, dispatch) {
  toast.error(reason, { duration: 4000 });
  if (order_id) {
    dispatch({ type: ACT.ORDER_ERROR_SET, payload: { order_id, reason } });
  }
}

// ─── Business day + shift ───────────────────────────────────────────
// These are the two calls the server requires BEFORE any order endpoint
// will accept work. We keep the reducer's BUSINESS_DAY_OPENED and
// SHIFT_OPENED optimistic dispatches so the UI unfreezes immediately,
// then reconcile with the server's canonical record. Rollback drops
// the reducer's state back to closed if the server refuses.
//
// NB: none of these thunks RE-THROW. Every branch resolves — either
// with the server response (success) or with `null` (surfaced error).
// Re-throwing a rejected axios promise into a React onClick handler
// crashes the app with an "AxiosError: Request failed with status ..."
// stack trace. Callers do not care about the value; they only care
// that the UI reflects the outcome. Toast + optional rollback is the
// whole contract.
export async function liveOpenBusinessDay(dispatch, getState, business_date, opened_by) {
  // POS Lifecycle Final Fix · SERVER-AUTHORITATIVE Business Day open.
  // Previously this thunk dispatched `BUSINESS_DAY_OPENED` optimistically
  // BEFORE `apiOpenBusinessDay` returned. That let the Floor unlock
  // tables and let the operator punch items while the server still
  // held a closed day; the next order create hit "No open business
  // day for this outlet" (400) from `_require_open_business_day`.
  //
  // New contract:
  //   1. Fire the server open.
  //   2. On success, re-hydrate from session-status so the reducer's
  //      business_date/business_day_id/opened_by/opened_at come
  //      straight from the persisted row — never a client guess.
  //   3. Only THEN dispatch `BUSINESS_DAY_OPENED` (the reducer is a
  //      mirror, not the source of truth).
  //   4. On failure, leave state untouched. The BusinessDayOpenDialog
  //      stays mounted and shows the backend error.
  const event_id = mintEventId();
  const tId = toast.loading("Opening business day…");
  try {
    await retryOnTimeout(() => apiOpenBusinessDay(business_date, event_id));
    // Server-authoritative rehydrate. If session-status doesn't yet
    // reflect the open (rare read-after-write lag on a Mongo primary),
    // we still fall back to dispatching BUSINESS_DAY_OPENED with the
    // requested date so the UI advances in lockstep — but only after
    // the write is confirmed on the server.
    let confirmedDate = business_date;
    let confirmedOpenedBy = opened_by || null;
    try {
      const status = await apiGetSessionStatus();
      if (status?.business_day?.business_date) {
        confirmedDate = status.business_day.business_date;
        confirmedOpenedBy = status.business_day.opened_by || confirmedOpenedBy;
      }
      // Fold the full status into the reducer so last_closed_business_date
      // and shift are refreshed alongside the day.
      dispatch({ type: ACT.SESSION_HYDRATED, payload: {
        business_day: status?.business_day ?? null,
        shift:        status?.shift ?? null,
        last_closed_business_date: status?.last_closed_business_date ?? null,
      }});
    } catch (_) { /* fall back to requested date */ }
    const dispatchPayload = { business_date: confirmedDate };
    if (confirmedOpenedBy) dispatchPayload.opened_by = confirmedOpenedBy;
    dispatch({ type: ACT.BUSINESS_DAY_OPENED, payload: dispatchPayload });
    toast.success("Business day open", { id: tId, duration: 1500 });
    return true;
  } catch (e) {
    // No optimistic dispatch to roll back — the reducer's business
    // day slot is untouched, tables stay locked, and the dialog
    // remains mounted so the manager can retry with a different
    // date or override.
    toast.error(`Could not open day: ${extractReason(e)}`, { id: tId, duration: 4000 });
    return false;
  }
}
export async function liveCloseBusinessDay(dispatch) {
  const event_id = mintEventId();
  const tId = toast.loading("Closing business day…");
  try {
    // POS Control Sprint · capture the server-resolved
    // `next_business_date` (computed per outlet timezone) and forward
    // it to the reducer so the next BusinessDayOpenDialog prefill
    // matches. Falls back to null → reducer's local +1-day logic.
    const r = await retryOnTimeout(() => apiCloseBusinessDay(event_id));
    dispatch({ type: ACT.BUSINESS_DAY_CLOSED, payload: {
      closed_by: "system",
      next_business_date: r?.next_business_date || null,
      outlet_timezone:    r?.outlet_timezone   || null,
    }});
    toast.success("Business day closed", { id: tId, duration: 1500 });
    // POS Cleanup Pass 3 · surface the server-resolved
    // `next_business_date` to the caller so the Close Day
    // orchestrator can immediately open the rolled-forward day
    // server-side (session-status must return it after a refresh).
    return { ok: true, next_business_date: r?.next_business_date || null,
             outlet_timezone: r?.outlet_timezone || null };
  } catch (e) {
    toast.error(`Could not close day: ${extractReason(e)}`, { id: tId, duration: 4000 });
    return { ok: false };
  }
}
export async function liveOpenShift(dispatch, payload) {
  // POS Cleanup Pass 3 · this is now invoked automatically on the
  // first cash-eligible PIN login of an open business day (see
  // POSTerminal auto-shift `useEffect`). The `silent` flag makes the
  // thunk swallow its progress + error toasts, which would otherwise
  // fire on every login even when the shift is already up server-side
  // (returns 409 "already open" — harmless because the reducer will
  // pick up the real shift on the next session-status refresh).
  const silent = !!(payload && payload.silent);
  const event_id = mintEventId();
  const tId = silent ? null : toast.loading("Opening shift…");
  try {
    const r = await retryOnTimeout(() => apiOpenShift(payload?.opening_float || 0, event_id));
    const serverShift = r?.shift || null;
    if (serverShift) {
      const enriched = { ...serverShift };
      if (!enriched.opened_by_name && payload?.opened_by) {
        enriched.opened_by_name = payload.opened_by;
      }
      dispatch({ type: ACT.SESSION_HYDRATED, payload: { shift: enriched } });
    }
    if (!silent) toast.success("Shift open", { id: tId, duration: 1500 });
    return true;
  } catch (e) {
    // 409 "shift already open" is the normal race path when a second
    // staff logs in on the same day — swallow it and re-hydrate.
    const reason = extractReason(e);
    const alreadyOpen = /already open|409/i.test(String(reason));
    if (alreadyOpen || silent) {
      try {
        const status = await apiGetSessionStatus();
        if (status?.shift) dispatch({ type: ACT.SESSION_HYDRATED, payload: { shift: status.shift } });
      } catch (_) { /* no-op */ }
      if (tId) toast.dismiss(tId);
      return alreadyOpen;
    }
    if (tId) toast.error(`Could not open shift: ${reason}`, { id: tId, duration: 4000 });
    return false;
  }
}
export async function liveCloseShift(dispatch) {
  // Financial Ground Truth (Feb 2026) · shift close.
  //
  // ▸ Shift Close is a SERVICE-shift terminator. It carries NO cash
  //   figures. Cash / EDC reconciliation belongs on Till Close.
  // ▸ Never optimistically flip local state — a client-side
  //   SHIFT_CLOSED before the server confirms would strand the
  //   Z-report against an actually-open shift.
  // ▸ The old client sent `declared_cash` and the server's
  //   `ShiftCloseIn` refused it with a FastAPI validation array,
  //   which extractReason then rendered as "[object Object]".
  const event_id = mintEventId();
  const tId = toast.loading("Closing shift…");
  try {
    await retryOnTimeout(() => apiCloseShift(event_id));
    toast.success("Shift closed", { id: tId, duration: 1500 });
    return { ok: true };
  } catch (e) {
    const reason = extractReason(e);
    toast.error(`Could not close shift: ${reason}`, { id: tId, duration: 4500 });
    return { ok: false, error: reason };
  }
}

// ─── Hydration ──────────────────────────────────────────────────────
// Called on POSTerminal mount when a live POS session is active.
// Pulls every OPEN order the server has for this outlet and feeds them
// into the reducer as fully-formed slices. Solves acceptance item 9:
// after a browser reload, the terminal must show every open order,
// every line, every kitchen note and every fired ticket exactly as
// they were before the reload.
export async function hydrateOpenOrders(dispatch) {
  try {
    // 1. Session status — business day + shift — must land before we
    //    seed the reducer with open orders. Without a `business_date`
    //    the reducer's TABLE_OPENED guard refuses new work.
    const status = await apiGetSessionStatus();
    // POS Lifecycle Final Fix · always dispatch SESSION_HYDRATED
    // with the full server response, including when
    // business_day / shift are null. The reducer clears any stale
    // local business_date when the server has no open day — the
    // "phantom 2026-08-10 last closed date" bug was caused by
    // conditionally skipping this dispatch on a null server day.
    dispatch({ type: ACT.SESSION_HYDRATED, payload: {
      business_day: status?.business_day ?? null,
      shift:        status?.shift ?? null,
      last_closed_business_date: status?.last_closed_business_date ?? null,
    }});
    // Sprint 3B correction · apply tenant idle-lock setting from the
    // server so a 30-minute (or admin-configured) timeout replaces the
    // reducer's compile-time default (previously 120s, unusable).
    const idle = Number(status?.pos_settings?.idle_lock_seconds);
    if (Number.isFinite(idle) && idle >= 60) {
      dispatch({ type: ACT.SESSION_IDLE_LOCK_SET, payload: { seconds: idle } });
    }
    // Financial Ground Truth (Feb 2026) · mirror the outlet's
    // invoice-config `rounding_mode` into the reducer so the OPEN-order
    // draft preview uses the same tax-rounding pipeline as the printed
    // invoice. Without this the guest sees a different amount before
    // and after Print Bill.
    const rmode = String(status?.invoice_config?.rounding_mode || "").toLowerCase();
    if (rmode === "total_only" || rmode === "per_component") {
      dispatch({ type: ACT.SESSION_ROUNDING_SET, payload: { mode: rmode } });
    }
    // 2. Open orders — rebuild each slice from the server view. Only
    //    fetches orders belonging to the currently OPEN business day
    //    (POS Cleanup Pass 1) — an OPEN order from yesterday must not
    //    resurrect on today's floor.
    const business_date = status?.business_day?.business_date || null;
    const orders = business_date ? await apiListOpenOrders(business_date) : [];
    if (Array.isArray(orders)) {
      for (const so of orders) {
        dispatch({ type: ACT.LIVE_ORDER_HYDRATED, payload: { server_order: so } });
      }
    }
    // 3. Current till + duration warning hours.
    //    A silent 4xx just means "no till". `till_open_warning_hours`
    //    is a tenant advisory the top bar consults for its amber nudge.
    //    POS Cleanup Pass 2 · The `cash_decision` payload from the
    //    manager-declare route is intentionally ignored — that prompt
    //    was removed and managers now use the same till float prompt
    //    as cashiers.
    try {
      const r = await axios.get(`${API}/api/pos/tills/current`, { headers: _headers() });
      const till = r.data?.till || null;
      const warnH = Number(r.data?.till_open_warning_hours);
      if (till) {
        dispatch({ type: ACT.TILL_HYDRATED, payload: { till } });
      }
      if (Number.isFinite(warnH) && warnH > 0) {
        dispatch({ type: ACT.TILL_WARN_HOURS_SET, payload: { hours: warnH } });
      }
    } catch (_) { /* no-op; UI will surface via top bar */ }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[pos] hydration failed", e);
  }
}

// ─── Till lifecycle · Sprint 2 B.2 ──────────────────────────────────
// A till is strictly per-staff. Every settlement gates on this. The
// server-authoritative shape lives in `pos_tills`; these thunks are
// the client's write path.
export async function liveOpenTill(dispatch, opening_float) {
  const event_id = mintEventId();
  const tId = toast.loading("Opening till…");
  try {
    const r = await retryOnTimeout(() => axios.post(
      `${API}/api/pos/tills/open`,
      { event_id, opening_float: Number(opening_float) || 0 },
      { headers: _headers() },
    ));
    dispatch({ type: ACT.TILL_OPENED, payload: { till: r.data.till } });
    toast.success(`Till open · float ₹${(Number(opening_float) || 0).toLocaleString("en-IN")}`,
      { id: tId, duration: 1800 });
    return r.data.till;
  } catch (e) {
    toast.error(`Could not open till: ${extractReason(e)}`, { id: tId, duration: 4000 });
    return null;
  }
}

export async function liveCloseTill(dispatch, declared_cash, extras = {}) {
  // POS Control Sprint · closeTill now forwards the full reconciliation
  // payload (denominations, counted/expected/variance, manager
  // takeover fields). `extras` is spread into the request so the same
  // call handles SELF, MANAGER_TAKEOVER and FLOAT_ONLY_MANAGER closes.
  const event_id = mintEventId();
  const tId = toast.loading("Closing till…");
  try {
    const r = await retryOnTimeout(() => axios.post(
      `${API}/api/pos/tills/close`,
      { event_id, declared_cash: Number(declared_cash) || 0, ...extras },
      { headers: _headers() },
    ));
    dispatch({ type: ACT.TILL_CLOSED, payload: { summary: r.data } });
    toast.success("Till closed", { id: tId, duration: 1500 });
    return r.data;
  } catch (e) {
    toast.error(`Could not close till: ${extractReason(e)}`, { id: tId, duration: 4000 });
    return null;
  }
}

// POS Control Sprint · list open tills for the current shift.
// Returns an array of tills, each enriched with `settlement_summary`
// (modes / cash_sales / expected_cash) so the open-tills blocker and
// the Till Close screen never recompute settlements client-side.
export async function liveListTills() {
  try {
    const r = await axios.get(`${API}/api/pos/tills`, { headers: _headers() });
    return Array.isArray(r?.data?.tills) ? r.data.tills : [];
  } catch (e) {
    return [];
  }
}
export async function liveListClosedTills() {
  try {
    const r = await axios.get(`${API}/api/pos/tills/closed`, { headers: _headers() });
    return Array.isArray(r?.data?.tills) ? r.data.tills : [];
  } catch (e) {
    return [];
  }
}

export async function liveTillTakeoverLock(till_id) {
  try {
    const r = await axios.post(
      `${API}/api/pos/tills/${till_id}/takeover-lock`,
      { event_id: `takeover_${till_id}_${Date.now()}`, handling_cash: false },
      { headers: _headers() },
    );
    return r.data;
  } catch (e) {
    toast.error(`Could not lock till: ${extractReason(e)}`, { duration: 3500 });
    return null;
  }
}

export async function liveManagerDeclareCash(_dispatch, _payload) {
  // POS Cleanup Pass 2 · Removed. The Manager cash decision prompt no
  // longer exists — managers use the same TillFloatPrompt as
  // cashiers, and the server-side manager-declare route is no longer
  // called from the client.
  return null;
}


// ─── Order lifecycle ────────────────────────────────────────────────
/** Open a dine-in order for a table. Awaits the server so the reducer
 *  slice is created with the AUTHORITATIVE order.id (no temp-id rebind
 *  gymnastics needed). Errors show a toast; the UI stays on the floor. */
export async function liveOpenTable(dispatch, payload) {
  const { table_id, covers, guest_phone, guest_name, authorisation_id } = payload || {};
  const event_id = mintEventId();      // stable across retries
  const tId = toast.loading("Opening table…");
  try {
    const order = await retryOnTimeout(() => apiOpenOrder({
      event_id,
      order_type: "DINE_IN", table_id, covers,
      guest_phone: guest_phone || "", guest_name: guest_name || "",
      // Sprint 8 · POS-QCS · manager approval id when covers > 999
      authorisation_id: authorisation_id || null,
    }));
    toast.success("Table opened", { id: tId, duration: 1500 });
    dispatch({ type: ACT.TABLE_OPENED, payload: {
      ...payload, id: order.id,
    }});
    return order;
  } catch (e) {
    const reason = extractReason(e);
    toast.error(`Could not open table: ${reason}`, { id: tId, duration: 4000 });
    return null;
  }
}

/** Open a non-dine-in order (takeaway / delivery / room service). */
export async function liveOpenOrder(dispatch, payload) {
  const { order_type, token, guest_phone, guest_name } = payload || {};
  const event_id = mintEventId();
  const tId = toast.loading("Opening order…");
  try {
    const order = await retryOnTimeout(() => apiOpenOrder({
      event_id,
      order_type, token: token || null,
      guest_phone: guest_phone || "", guest_name: guest_name || "",
    }));
    toast.success("Order opened", { id: tId, duration: 1500 });
    dispatch({ type: ACT.ORDER_OPENED, payload: {
      ...payload, id: order.id, type: order_type, token: order.token,
    }});
    return order;
  } catch (e) {
    const reason = extractReason(e);
    toast.error(`Could not open order: ${reason}`, { id: tId, duration: 4000 });
    return null;
  }
}

/** Hold — optimistic. Reducer flips HELD immediately; on failure we
 *  roll back to OPEN and stamp the reason on the order banner. */
export async function liveHoldOrder(dispatch, getState, order_id, reason = "") {
  const snapshot = getState().orders?.[order_id];
  if (!snapshot) return;
  const event_id = mintEventId();
  dispatch({ type: ACT.ORDER_HELD });
  dispatch({ type: ACT.ORDER_PENDING_SET, payload: { order_id, event_id } });
  try {
    const order = await retryOnTimeout(() =>
      apiHoldOrder(order_id, reason, event_id)
    );
    dispatch({ type: ACT.ORDER_SYNCED_FROM_SERVER, payload: { server_order: order } });
    dispatch({ type: ACT.ORDER_PENDING_CLEAR, payload: { order_id } });
  } catch (e) {
    dispatch({ type: ACT.SLICE_ROLLBACK, payload: { order_id, snapshot } });
    surfaceError(order_id, `Hold rejected: ${extractReason(e)}`, dispatch);
  }
}

/** Resume — inverse of hold, same rollback semantics. */
export async function liveResumeOrder(dispatch, getState, order_id) {
  const snapshot = getState().orders?.[order_id];
  if (!snapshot) return;
  const event_id = mintEventId();
  dispatch({ type: ACT.ORDER_RESUMED });
  dispatch({ type: ACT.ORDER_PENDING_SET, payload: { order_id, event_id } });
  try {
    const order = await retryOnTimeout(() => apiResumeOrder(order_id, event_id));
    dispatch({ type: ACT.ORDER_SYNCED_FROM_SERVER, payload: { server_order: order } });
    dispatch({ type: ACT.ORDER_PENDING_CLEAR, payload: { order_id } });
  } catch (e) {
    dispatch({ type: ACT.SLICE_ROLLBACK, payload: { order_id, snapshot } });
    surfaceError(order_id, `Resume rejected: ${extractReason(e)}`, dispatch);
  }
}

// ─── Line lifecycle ─────────────────────────────────────────────────
/** Add a line — optimistic reducer punch, then persist. On success we
 *  rename the reducer's temp line id (`ln_xxx`) to the server's line id
 *  so subsequent edits can address the same row. On failure we roll the
 *  slice back to its pre-punch snapshot. */
export async function liveAddLine(dispatch, getState, order_id, item) {
  const snapshot = getState().orders?.[order_id];
  if (!snapshot) return;
  const event_id = mintEventId();
  // Pre-mint the local line id here so we never have to race the
  // React render cycle to find "the last-added line". A rapid tap
  // storm used to converge on the wrong row because getState() was
  // reading pre-dispatch state (stateRef updates in a useEffect that
  // runs after commit). Passing localLineId into the payload makes
  // the reducer use it directly and gives us a stable handle for
  // pending markers and the eventual server-id rebind.
  const localLineId = "ln_" + Math.random().toString(36).slice(2, 12);
  dispatch({ type: ACT.LINE_ADDED, payload: {
    item_id:  item.id,
    name:     item.name,
    price:    Number(item.price) || 0,
    routing:  item.routing || "kitchen",
    note:     item.note || "",
    local_line_id: localLineId,
  }});
  // Match the just-added line by item_id + note rather than by array
  // position. Rapid taps used to race here — the pre-minted
  // localLineId above eliminates that race entirely; this block only
  // catches the aggregation case (repeat-tap merged into an existing
  // line, no fresh row) so we can still surface a pending marker on
  // the row the cashier just touched.
  const after = getState().orders?.[order_id];
  const matchLine = (after?.lines || [])
    .filter((l) => l.ticket_id == null
                && l.item_id === item.id
                && (l.note || "") === (item.note || ""))
    .pop();
  const trackedLineId = matchLine?.id || localLineId;
  if (trackedLineId) {
    dispatch({ type: ACT.LINE_PENDING_SET, payload: { order_id, line_id: trackedLineId, event_id } });
  }
  try {
    // Sprint 3B · include tax_regime + tax_rate so the SERVER snapshots
    // the same tax the guest sees on the bill preview. The routing
    // decides the regime: `bar` → 20% VAT, everything else → 5% GST.
    // Without this, server invoice_total drops to subtotal-only and
    // the cashier over-tenders (bug found in iter336).
    const routing = item.routing || "kitchen";
    const isLiquor = routing === "bar";
    const resp = await retryOnTimeout(() => apiAddLine(order_id, {
      event_id,
      item_id: item.id,
      item_name_snapshot: item.name,
      qty: 1,
      rate_snapshot: Number(item.price) || 0,
      routing,
      kitchen_note: item.note || "",
      tax_regime: isLiquor ? "VAT" : "GST",
      tax_rate:   isLiquor ? 20 : 5,
    }));
    // Rebind: the server may have merged with an existing line — its
    // response's `line_id` is authoritative. Rename in the reducer.
    if (trackedLineId && resp?.line_id) {
      dispatch({ type: ACT.LINE_ID_REBOUND, payload: {
        order_id, local_line_id: trackedLineId, server_line_id: resp.line_id,
      }});
    }
    dispatch({ type: ACT.ORDER_SYNCED_FROM_SERVER, payload: { server_order: resp.order } });
    dispatch({ type: ACT.LINE_PENDING_CLEAR, payload: { order_id, line_id: resp.line_id || trackedLineId } });
    // POS UX closure (Feb 2026) · return an ok flag so the caller can
    // clear the search bar and refocus it ONLY after a successful punch.
    return { ok: true };
  } catch (e) {
    dispatch({ type: ACT.SLICE_ROLLBACK, payload: { order_id, snapshot } });
    surfaceError(order_id, `Could not add ${item.name}: ${extractReason(e)}`, dispatch);
    return { ok: false, error: e };
  }
}

/** Change qty — optimistic. */
export async function liveChangeLineQty(dispatch, getState, order_id, line_id, qty, meta = {}) {
  const snapshot = getState().orders?.[order_id];
  if (!snapshot) return;
  const event_id = mintEventId();
  dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id, qty, ...meta } });
  dispatch({ type: ACT.LINE_PENDING_SET, payload: { order_id, line_id, event_id } });
  try {
    const r = await retryOnTimeout(() => axios.patch(
      `${API}/api/pos/orders/${order_id}/lines/${line_id}`,
      {
        event_id, qty,
        reason_code: meta.reason_code || "",
        authorised_by: meta.authorised_by || "",
        // Sprint 8 · POS-QCS · pass manager authorisation id so the
        // server accepts a qty > 999 (up to 10,000). Undefined for
        // normal-range changes.
        authorisation_id: meta.authorisation_id || null,
      },
      { headers: _headers() },
    ));
    dispatch({ type: ACT.ORDER_SYNCED_FROM_SERVER, payload: { server_order: r.data.order } });
    dispatch({ type: ACT.LINE_PENDING_CLEAR, payload: { order_id, line_id } });
    return { ok: true, order: r.data.order };
  } catch (e) {
    dispatch({ type: ACT.SLICE_ROLLBACK, payload: { order_id, snapshot } });
    surfaceError(order_id, `Quantity change rejected: ${extractReason(e)}`, dispatch);
    return { ok: false, error: e };
  }
}

/** Save a kitchen note on an existing line — persists via PATCH. */
export async function liveSetLineNote(dispatch, getState, order_id, line_id, note) {
  const snapshot = getState().orders?.[order_id];
  if (!snapshot) return;
  const event_id = mintEventId();
  dispatch({ type: ACT.LINE_NOTE_SET, payload: { line_id, note } });
  dispatch({ type: ACT.LINE_PENDING_SET, payload: { order_id, line_id, event_id } });
  try {
    const r = await retryOnTimeout(() => axios.patch(
      `${API}/api/pos/orders/${order_id}/lines/${line_id}`,
      { event_id, kitchen_note: note || "" },
      { headers: _headers() },
    ));
    dispatch({ type: ACT.ORDER_SYNCED_FROM_SERVER, payload: { server_order: r.data.order } });
    dispatch({ type: ACT.LINE_PENDING_CLEAR, payload: { order_id, line_id } });
  } catch (e) {
    dispatch({ type: ACT.SLICE_ROLLBACK, payload: { order_id, snapshot } });
    surfaceError(order_id, `Note save rejected: ${extractReason(e)}`, dispatch);
  }
}

/** Void an unfired line — optimistic delete + persist. */
export async function liveVoidLine(dispatch, getState, order_id, line_id, reason_code = "") {
  const snapshot = getState().orders?.[order_id];
  if (!snapshot) return;
  const event_id = mintEventId();
  dispatch({ type: ACT.LINE_VOIDED, payload: { line_id } });
  try {
    await retryOnTimeout(() => axios.delete(
      `${API}/api/pos/orders/${order_id}/lines/${line_id}`,
      { headers: _headers(), data: { event_id, reason_code } },
    ));
  } catch (e) {
    dispatch({ type: ACT.SLICE_ROLLBACK, payload: { order_id, snapshot } });
    surfaceError(order_id, `Void rejected: ${extractReason(e)}`, dispatch);
  }
}

/** Fire the pending tickets on the order. */
export async function liveFireTicket(dispatch, getState, order_id) {
  const snapshot = getState().orders?.[order_id];
  if (!snapshot) return;
  const event_id = mintEventId();
  dispatch({ type: ACT.TICKET_SENT });
  dispatch({ type: ACT.ORDER_PENDING_SET, payload: { order_id, event_id } });
  try {
    const resp = await retryOnTimeout(() => apiFireTicket(order_id, event_id));
    dispatch({ type: ACT.ORDER_SYNCED_FROM_SERVER, payload: { server_order: resp.order } });
    dispatch({ type: ACT.ORDER_PENDING_CLEAR, payload: { order_id } });
    toast.success(`Ticket fired · ${(resp.tickets || []).length} destination(s)`, { duration: 1500 });
  } catch (e) {
    dispatch({ type: ACT.SLICE_ROLLBACK, payload: { order_id, snapshot } });
    surfaceError(order_id, `Fire rejected: ${extractReason(e)}`, dispatch);
  }
}

// ─── Internals ──────────────────────────────────────────────────────
function _headers() {
  const t = window.localStorage.getItem("pos_token");
  let terminal_id = "unbound";
  try {
    const b = JSON.parse(window.localStorage.getItem("pos_terminal_binding") || "null");
    if (b?.terminal_id) terminal_id = b.terminal_id;
  } catch (_) { /* keep default */ }
  return {
    Authorization: t ? `Bearer ${t}` : "",
    "X-Pos-Terminal": terminal_id,
    "Content-Type": "application/json",
  };
}

// ─── Hook: useLivePos ───────────────────────────────────────────────
// Call sites use this instead of dispatching mutation actions
// directly. In DEMO mode (`live=false`) or when no POS token is
// present, every method just dispatches to the local reducer. In
// LIVE mode, mutations flow through the async thunks above.
//
// `stateRef` is a ref that ALWAYS holds the freshest state — do NOT
// pass a stale `state` prop; the thunks read from getState() at
// closure time to capture pre-optimistic snapshots for rollback.
import { useCallback, useMemo } from "react";

export function useLivePos({ dispatch, stateRef, live }) {
  const getState = useCallback(() => stateRef.current, [stateRef]);

  const surface = useMemo(() => ({
    // POS Cleanup Pass 1 · expose `isLive` on the surface so callers
    // can check `livePos.isLive` before choosing between the server
    // path and a local reducer path. Historically callers used
    // `if (livePos) …` — but useLivePos ALWAYS returned an object
    // regardless of the `live` flag, so the guard was useless and
    // demo-mode paths silently minted local uid("ord") orders even
    // on real /pos when live=true. Callers migrated to this flag.
    isLive: !!live,
    // Order lifecycle
    openTable: async (payload) => {
      if (!live) {
        dispatch({ type: ACT.TABLE_OPENED, payload });
        return;
      }
      return liveOpenTable(dispatch, payload);
    },
    openOrder: async (payload) => {
      if (!live) {
        // Reducer expects `type` key; live thunk expects `order_type`.
        // Normalise on the reducer path.
        const { order_type, ...rest } = payload;
        dispatch({ type: ACT.ORDER_OPENED, payload: { type: order_type, ...rest } });
        return;
      }
      return liveOpenOrder(dispatch, payload);
    },
    holdActive: async (order_id, reason = "") => {
      if (!live) { dispatch({ type: ACT.ORDER_HELD }); return; }
      return liveHoldOrder(dispatch, getState, order_id, reason);
    },
    resumeActive: async (order_id) => {
      if (!live) { dispatch({ type: ACT.ORDER_RESUMED }); return; }
      return liveResumeOrder(dispatch, getState, order_id);
    },
    // Lines
    addLine: async (order_id, item) => {
      if (!live) {
        // Sprint UX closure (Feb 2026) · forward the FULL snapshot the
        // caller froze in onTileTap (name/price/routing) so the reducer
        // does not fall back to the demo menu — a mismatch there would
        // silently drop the punch. Matches the old direct-dispatch
        // payload BillingScreen used before it routed through livePos.
        dispatch({ type: ACT.LINE_ADDED, payload: {
          item_id: item.id,
          name:    item.name,
          price:   Number(item.price) || 0,
          routing: item.routing || "kitchen",
          note:    item.note || "",
        }});
        return { ok: true };
      }
      return liveAddLine(dispatch, getState, order_id, item);
    },
    changeLineQty: async (order_id, line_id, qty, meta = {}) => {
      if (!live) {
        dispatch({ type: ACT.LINE_QTY_CHANGED, payload: { line_id, qty, ...meta } });
        return;
      }
      return liveChangeLineQty(dispatch, getState, order_id, line_id, qty, meta);
    },
    setLineNote: async (order_id, line_id, note) => {
      if (!live) {
        dispatch({ type: ACT.LINE_NOTE_SET, payload: { line_id, note } });
        return;
      }
      return liveSetLineNote(dispatch, getState, order_id, line_id, note);
    },
    voidLine: async (order_id, line_id, reason_code = "") => {
      if (!live) {
        dispatch({ type: ACT.LINE_VOIDED, payload: { line_id } });
        return;
      }
      return liveVoidLine(dispatch, getState, order_id, line_id, reason_code);
    },
    fireTicket: async (order_id) => {
      if (!live) { dispatch({ type: ACT.TICKET_SENT }); return; }
      return liveFireTicket(dispatch, getState, order_id);
    },
    // ── Sprint 3B · Fiscal path · print / tender / settle ───────────
    // These thunks replace the reducer's local invoice allocator and
    // local settlement path. The server owns the fiscal ledger and
    // returns real invoice numbers and tender ids; the reducer only
    // mirrors what the server just confirmed.
    printBill: async (order_id) => {
      if (!live) { dispatch({ type: ACT.BILL_PRINTED }); return true; }
      // Stable event_id across retries (Financial Ground Truth · Feb 2026)
      const event_id = mintEventId();
      const tId = toast.loading("Allocating invoice number…");
      try {
        const r = await retryOnTimeout(() => apiPrintBill(order_id, null, event_id));
        toast.success(`Invoice ${r.invoice.invoice_no}`, { id: tId, duration: 1500 });
        // Adopt the server invoice number verbatim (contract §3, §7).
        dispatch({ type: ACT.BILL_PRINTED, payload: {
          invoice_no: r.invoice.invoice_no,
          invoice:    r.invoice,
        }});
        return r.invoice.invoice_no;
      } catch (e) {
        toast.error(`Print refused: ${extractReason(e)}`, { id: tId, duration: 4000 });
        return null;
      }
    },
    reprintBill: async (order_id, audit = null) => {
      // POS Control Sprint · when the caller supplies an audit payload
      // (reason_code / notes / authorised_by), we forward it to the
      // server AND mirror it on the reducer so the Exception Report
      // and the receipt watermark surface the same event locally.
      if (!live) {
        dispatch({ type: ACT.BILL_REPRINTED, payload: audit || null });
        return true;
      }
      const event_id = mintEventId();
      try {
        await retryOnTimeout(() => apiReprintBill(order_id, event_id, {
          reason:                 audit?.reason_code || undefined,
          notes:                  audit?.notes || undefined,
          authorised_by_staff_id: audit?.authorised_by_id || audit?.authorised_by || undefined,
        }));
        dispatch({ type: ACT.BILL_REPRINTED, payload: audit || null });
        return true;
      } catch (e) {
        toast.error(`Reprint refused: ${extractReason(e)}`, { duration: 4000 });
        return false;
      }
    },
    // ── Add-Items-after-Print · server-backed void + reopen ─────────
    // Voids the specified invoice ON THE SERVER and returns the order
    // to OPEN so the cashier can punch more items. The next print
    // allocates a fresh invoice number covering ALL lines. The old
    // invoice remains recorded as `voided` with the manager's
    // authoriser + reason for the Exception Report.
    // ── Release Draft · POS Cleanup Pass 1 ────────────────────────
    // Non-transactional discard for empty / unsent / stale drafts.
    // Never used for fired or printed orders.
    releaseDraft: async (order_id, reason = "EMPTY_BACK") => {
      if (!live) {
        dispatch({ type: ACT.ORDER_DRAFT_RELEASED, payload: { order_id, reason } });
        return { ok: true };
      }
      const event_id = mintEventId();
      try {
        const r = await retryOnTimeout(() => apiReleaseDraftOrder(order_id, reason, event_id));
        dispatch({ type: ACT.ORDER_DRAFT_RELEASED, payload: {
          order_id: r?.order_id || order_id, reason: r?.reason || reason,
        }});
        return { ok: true, ...r };
      } catch (e) {
        toast.error(`Cannot release: ${extractReason(e)}`, { duration: 4000 });
        return { ok: false, error: extractReason(e) };
      }
    },

    // ── Void order · POS Cleanup Pass 1 ────────────────────────────
    // Canonical whole-order void. Server cascades to invoice void
    // (if any) and marks every line voided. Manager PIN authorised
    // via the SharedPinDialog before we reach this thunk.
    voidOrder: async ({ order_id, reason, authorised_by_staff_id }) => {
      if (!live) {
        dispatch({ type: ACT.ORDER_VOIDED, payload: {
          reason, authorised_by: authorised_by_staff_id, at: Date.now(),
        }});
        return { ok: true };
      }
      const event_id = mintEventId();
      const tId = toast.loading("Voiding order…");
      try {
        const r = await retryOnTimeout(() =>
          apiVoidOrder(order_id, reason, authorised_by_staff_id, event_id));
        toast.success("Order voided", { id: tId, duration: 1500 });
        if (r?.order) {
          dispatch({ type: ACT.LIVE_ORDER_HYDRATED, payload: { server_order: r.order } });
        } else {
          dispatch({ type: ACT.ORDER_VOIDED, payload: {
            reason, authorised_by: r?.authorised_by_name || authorised_by_staff_id, at: Date.now(),
          }});
        }
        return { ok: true, ...r };
      } catch (e) {
        toast.error(`Void refused: ${extractReason(e)}`, { id: tId, duration: 4000 });
        return { ok: false, error: extractReason(e) };
      }
    },

    // ── Split order · POS Control Sprint (Feb 2026) ─────────────────
    // Persist a bill split on the server so every child gets a REAL
    // order id, real line rows and preserves the parent's ticket
    // assignments. Without this, `livePos.fireTicket` / `printBill` on
    // a reducer-only child id 404's with "Order not found" — the bug
    // report that motivated this thunk.
    //
    // Behaviour:
    //   • Live mode: POST /api/pos/orders/{parent}/split, then hydrate
    //     the parent and every returned child through
    //     LIVE_ORDER_HYDRATED so the SplitChildrenView reads the
    //     server-canonical shape (correct ids, preserved ticket_id
    //     per line, echoed split_label).
    //   • Demo mode: fall back to the reducer-only ORDER_SPLIT — the
    //     demo has never talked to the backend and its child ids are
    //     never re-used for server calls.
    splitOrder: async ({ order_id, groups }) => {
      if (!live) {
        dispatch({ type: ACT.ORDER_SPLIT, payload: { source_order_id: order_id, groups } });
        return { ok: true };
      }
      const event_id = mintEventId();
      const tId = toast.loading("Splitting bill…");
      try {
        const r = await retryOnTimeout(() => apiSplitOrder(order_id, groups, event_id));
        toast.success(`Split into ${(r?.children || []).length} bills`, { id: tId, duration: 1500 });
        // Hydrate parent first (removes moved lines, marks
        // split_children) then every child in server order — the
        // SplitChildrenView reads `childrenOf(state, parent)` which
        // depends on `split_from` being set on the child hydrated slice.
        if (r?.order) {
          dispatch({ type: ACT.LIVE_ORDER_HYDRATED, payload: { server_order: r.order } });
        }
        (r?.children || []).forEach((c) => {
          dispatch({ type: ACT.LIVE_ORDER_HYDRATED, payload: { server_order: c } });
        });
        return { ok: true, children: r?.children || [] };
      } catch (e) {
        toast.error(`Split refused: ${extractReason(e)}`, { id: tId, duration: 4000 });
        return { ok: false, error: extractReason(e) };
      }
    },
    // ── Move a printed unpaid invoice to Outstanding Dues ──────────
    // The tax is still due (the sale happened); this ledger records
    // that no cash was received today. Manager auth + reason required
    // (server enforces both). Used at business-day close so cashiers
    // can proceed without a pending settlement.
    transferInvoiceToDues: async ({ order_id, invoice_no,
                                     reason_type = "uncollected",
                                     reason_note,
                                     party_name,
                                     authorised_by_staff_id }) => {
      if (!live) {
        // Demo mode: local no-op (matches reducer behaviour today).
        return { ok: true };
      }
      const event_id = mintEventId();
      const tId = toast.loading("Moving to outstanding dues…");
      try {
        const r = await retryOnTimeout(() => apiTransferInvoiceToDues(
          invoice_no, {
            reason_type, reason_note, party_name,
            authorised_by_staff_id, event_id,
          }));
        toast.success(`Moved ${invoice_no} to dues`, { id: tId, duration: 1800 });
        if (r?.order) {
          dispatch({ type: ACT.LIVE_ORDER_HYDRATED, payload: { server_order: r.order } });
        }
        return { ok: true, dues: r?.dues, invoice: r?.invoice, order: r?.order };
      } catch (e) {
        toast.error(`Move refused: ${extractReason(e)}`, { id: tId, duration: 4000 });
        return { ok: false, error: extractReason(e) };
      }
    },
    voidInvoiceForReopen: async ({ order_id, invoice_no, reason,
                                    authorised_by_staff_id }) => {
      // POS Control Sprint · `reason` is now the authoritative
      // reason_code (e.g. POST_PRINT_DISCOUNT, REOPENED_FOR_ADDITIONAL_ITEMS).
      // We preserve the legacy fallback so pre-existing callers that
      // omit `reason` still land on the "reopen for additional items"
      // audit; new callers (Discount-after-print) explicitly pass
      // their own code and it flows through to both the API and the
      // reducer mirror.
      const REASON_CODE = reason || "REOPENED_FOR_ADDITIONAL_ITEMS";
      if (!live) {
        dispatch({ type: ACT.INVOICE_VOIDED, payload: {
          source_order_id: order_id,
          voided_invoice:  invoice_no,
          authorised_by:   authorised_by_staff_id || "manager",
          at:              Date.now(),
          reason_code:     REASON_CODE,
          note:            null,
        }});
        return { ok: true };
      }
      const event_id = mintEventId();
      const tId = toast.loading("Voiding invoice…");
      try {
        const r = await retryOnTimeout(() => apiVoidInvoice(invoice_no, {
          reason: REASON_CODE,
          authorised_by_staff_id, event_id,
        }));
        toast.success(`Invoice ${invoice_no} voided`, { id: tId, duration: 1500 });
        // Mirror on the reducer so the UI immediately reflects the
        // server outcome: banner clears, order returns to OPEN, the
        // voided invoice list gains an entry for the Exception Report.
        dispatch({ type: ACT.INVOICE_VOIDED, payload: {
          source_order_id: order_id,
          voided_invoice:  invoice_no,
          authorised_by:   r?.invoice?.void_authorised_by_name
                            || r?.invoice?.void_authorised_by
                            || authorised_by_staff_id,
          at:              Date.now(),
          reason_code:     REASON_CODE,
          note:            null,
        }});
        // Rehydrate the reopened order from the server response so
        // state/invoice_no reset happen atomically with the reducer's
        // local flip. Server sends `order` in the void response.
        if (r?.order) {
          dispatch({ type: ACT.LIVE_ORDER_HYDRATED, payload: { server_order: r.order } });
        }
        return { ok: true, invoice: r?.invoice, order: r?.order };
      } catch (e) {
        toast.error(`Void refused: ${extractReason(e)}`, { id: tId, duration: 4000 });
        return { ok: false, error: extractReason(e) };
      }
    },
    addTender: async (order_id, tender) => {
      if (!live) {
        dispatch({ type: ACT.PAYMENT_TENDERED, payload: tender });
        return { ok: true, balance_due: 0 };
      }
      // Client-side mode names (from PaymentOverlay) map to the server
      // registry in pos_settlement.py. Keep the mapping small and
      // explicit so a mode rename never silently drops a tender.
      const MODE_MAP = {
        cash:           "cash",
        card:           "card",
        upi:            "upi",
        room_posting:   "room",
        swiggy_dineout: "swiggy",
        zomato:         "zomato",
        due_credit:     "due",
        complimentary:  "complimentary",
        // pay_online is not a fiscal mode; drop through as "upi" so
        // the reference (PSP payment link) validates as ≥4 chars.
        pay_online:     "upi",
      };
      const server_mode = MODE_MAP[tender.mode] || tender.mode;
      // Financial Ground Truth (Feb 2026) · mint event_id ONCE
      // before entering the retry loop, so retries share the key.
      // Passing it to the API layer explicitly ensures `withEvent()`
      // does NOT re-mint on the second attempt (root cause of the
      // duplicate-tender bug on ROLL/26-27/0011: 5 event_ids for the
      // same ₹1,620 cash tender, ~200ms apart).
      const event_id = mintEventId();
      const tId = toast.loading("Recording payment…");
      try {
        const r = await retryOnTimeout(() => apiAddTender(order_id, {
          mode:               server_mode,
          amount:             Number(tender.amount) || 0,
          reference:          tender.reference || null,
          comp_reason:        tender.comp_reason || null,
          comp_authorised_by: tender.authorised_by || null,
          // Financial Ground Truth (Feb 2026) · cash overpayment.
          // The overlay may pass an `amount_received` distinct from
          // `amount` (the amount applied to the invoice); the server
          // uses `amount` for reconciliation and stores
          // `amount_received` + `change_due` for the receipt.
          amount_received:    (tender.amount_received != null
                                ? Number(tender.amount_received)
                                : undefined),
          event_id,
        }));
        toast.success("Payment recorded", { id: tId, duration: 1200 });
        // Mirror on the reducer so the UI reflects the server tender
        // immediately — using the SERVER's tender id (never a local
        // "pay_xxx"), so reverse & reference-update address the right row.
        // Keep the CLIENT mode name in the reducer so downstream UI
        // (mode chips, keyboard chain) stays coherent; server_mode is
        // the fiscal record we already persisted.
        dispatch({ type: ACT.PAYMENT_TENDERED, payload: {
          id:            r.tender.id,
          mode:          tender.mode,
          amount:        r.tender.amount,
          // Financial Ground Truth (Feb 2026) · thread server-side
          // amount_received + change_due into the reducer so the
          // settled panel + receipt can render "Cash received / Change
          // due" from the server truth, not local subtraction.
          amount_received: r.tender.amount_received,
          change_due:      r.tender.change_due,
          reference:     r.tender.reference,
          status:        "confirmed",
          authorised_by: r.tender.comp_authorised_by || null,
          comp_reason:   r.tender.comp_reason || null,
          at:            Date.now(),
        }});
        return { ok: true, balance_due: r.balance_due,
                 invoice_total: r.invoice_total, tender: r.tender };
      } catch (e) {
        toast.error(`Payment refused: ${extractReason(e)}`, { id: tId, duration: 4000 });
        return { ok: false, error: extractReason(e) };
      }
    },
    reverseTender: async (tender_id, { reason = "", authorised_by_staff_id = null } = {}) => {
      if (!live) {
        dispatch({ type: ACT.PAYMENT_REVERSED, payload: { payment_id: tender_id } });
        return true;
      }
      // Stable event_id across retries (Financial Ground Truth · Feb 2026)
      const event_id = mintEventId();
      try {
        await retryOnTimeout(() => apiReverseTender(tender_id, {
          reason, authorised_by_staff_id, event_id,
        }));
        dispatch({ type: ACT.PAYMENT_REVERSED, payload: { payment_id: tender_id } });
        return true;
      } catch (e) {
        toast.error(`Reversal refused: ${extractReason(e)}`, { duration: 4000 });
        return false;
      }
    },
    settleOrder: async (order_id) => {
      if (!live) { dispatch({ type: ACT.ORDER_SETTLED }); return true; }
      // Stable event_id across retries (Financial Ground Truth · Feb 2026)
      const event_id = mintEventId();
      const tId = toast.loading("Completing settlement…");
      try {
        const r = await retryOnTimeout(() => apiSettleOrder(order_id, event_id));
        toast.success("Order settled", { id: tId, duration: 1500 });
        dispatch({ type: ACT.ORDER_SETTLED });
        return { ok: true, order: r.order };
      } catch (e) {
        toast.error(`Settle refused: ${extractReason(e)}`, { id: tId, duration: 4000 });
        return { ok: false, error: extractReason(e) };
      }
    },
    // Business day + shift lifecycle
    openBusinessDay: async (business_date, opened_by) => {
      if (!live) {
        dispatch({ type: ACT.BUSINESS_DAY_OPENED,
          payload: { business_date, opened_by: opened_by || "system" } });
        return;
      }
      return liveOpenBusinessDay(dispatch, getState, business_date, opened_by);
    },
    closeBusinessDay: async () => {
      if (!live) {
        dispatch({ type: ACT.BUSINESS_DAY_CLOSED, payload: { closed_by: "system" } });
        return;
      }
      return liveCloseBusinessDay(dispatch);
    },
    // POS Cleanup Pass 3 · canonical unresolved-orders fetch. The
    // BusinessDayCloseDialog calls this so the blocker list matches
    // exactly what /pos/business-days/close will refuse on (same
    // server-side predicate). No demo fallback — non-live sessions
    // never gate on server state.
    fetchUnresolvedOrders: async (business_date) => {
      if (!live) return { business_date: null, orders: [] };
      try {
        return await apiFetchUnresolvedOrders(business_date);
      } catch (e) {
        toast.error(`Could not load unresolved orders: ${extractReason(e)}`, { duration: 3500 });
        return { business_date: null, orders: [], error: extractReason(e) };
      }
    },
    openShift: async (payload) => {
      if (!live) { dispatch({ type: ACT.SHIFT_OPENED, payload }); return; }
      return liveOpenShift(dispatch, payload);
    },
    closeShift: async (declared_cash) => {
      if (!live) return true;
      return liveCloseShift(dispatch, declared_cash);
    },
    // Till lifecycle · Sprint 2 B.2
    openTill: async (opening_float) => {
      if (!live) {
        const st = getState();
        const staff = st.session?.staff_snapshot
          || (st.session?.logged_in_staff_id ? findStaffById(st.session.logged_in_staff_id) : null)
          || {};
        dispatch({ type: ACT.TILL_OPENED, payload: { till: {
          id: `demo_till_${Date.now()}`,
          staff_id:   staff.id   || "demo",
          staff_name: staff.name || "Demo cashier",
          opening_float: Number(opening_float) || 0,
          opened_at: new Date().toISOString(),
        }}});
        return true;
      }
      return liveOpenTill(dispatch, opening_float);
    },
    closeTill: async (declared_cash, extras = {}) => {
      if (!live) {
        const st = getState();
        const till  = st.session?.current_till || {};
        const staff = st.session?.staff_snapshot
          || (st.session?.logged_in_staff_id ? findStaffById(st.session.logged_in_staff_id) : null)
          || {};
        dispatch({ type: ACT.TILL_CLOSED, payload: { summary: {
          till_id:        till.id,
          staff_id:       till.staff_id   || staff.id   || "demo",
          staff_name:     till.staff_name || staff.name || "Demo cashier",
          opening_float:  Number(till.opening_float) || 0,
          declared_cash:  Number(declared_cash) || 0,
          expected_cash:  null,   // Sprint 3 fiscal path computes this
          variance:       null,
        }}});
        return true;
      }
      return liveCloseTill(dispatch, declared_cash, extras);
    },
    // POS Control Sprint · surface the open-till list to the UI.
    // Returns [] in demo mode (the reducer already knows about the
    // single demo till via state.session.current_till).
    listTills: async () => {
      if (!live) return [];
      return liveListTills();
    },
    // POS Control Sprint · load persisted closed tills for the
    // current shift so the ShiftCloseSummaryDialog reads server state
    // instead of an orchestrator-local buffer.
    listClosedTills: async () => {
      if (!live) return [];
      return liveListClosedTills();
    },
    // Lock a cashier's till against further transactions while a
    // manager takeover close is in progress. Manager PIN was already
    // validated at the UI layer before calling this.
    tillTakeoverLock: async (till_id) => {
      if (!live) return { ok: true };
      const r = await liveTillTakeoverLock(till_id);
      return r ? { ok: true, ...r } : { ok: false };
    },
    declareCash: async (_payload) => {
      // POS Cleanup Pass 2 · The manager cash decision prompt was
      // removed. This surface method is a no-op retained only so any
      // straggling call site doesn't crash — new code MUST use
      // `openTill(opening_float)` directly.
      return { ok: true };
    },
    dismissTillPrompt: () => {
      dispatch({ type: ACT.TILL_PROMPT_DISMISSED });
    },
    // Hydration on mount / reload
    hydrate: async () => {
      if (!live) return;
      return hydrateOpenOrders(dispatch);
    },
    // Utility: dismiss an inline order error banner.
    clearOrderError: (order_id) => {
      dispatch({ type: ACT.ORDER_ERROR_CLEAR, payload: { order_id } });
    },
  }), [dispatch, getState, live]);
  return surface;
}

